<script setup lang="ts">
import { computed } from 'vue';
import type { Image } from '@sanity/types';

interface PartnerLogoData {
  name?: string;
  partnerName?: string;
  partnerLogo?: Image;
  _key?: string;
}

const props = withDefaults(defineProps<{
  partnerLogo: PartnerLogoData;
  optimizedImage?: Image | null;
  imageOptions?: {
    width?: number;
    height?: number;
    quality?: number;
    fit?: string;
  };
  altText?: string;
  hasValidLogo?: boolean;
}>(), {
  optimizedImage: undefined,
  imageOptions: undefined,
  altText: undefined,
  hasValidLogo: undefined,
});

const finalImage = computed((): string | null => {
  const imageData = props.optimizedImage || props.partnerLogo?.partnerLogo;

  if (!imageData || !imageData.asset?._ref) {
    return null;
  }

  const { $urlFor } = useNuxtApp();
  let builder = $urlFor(imageData);

  if (props.imageOptions) {
    if (props.imageOptions.width) {
      builder = builder.width(props.imageOptions.width);
    }
    if (props.imageOptions.quality) {
      builder = builder.quality(props.imageOptions.quality);
    }
  } else {
    builder = builder.width(256).quality(85);
  }
  return builder.url();
});

const finalAltText = computed((): string | undefined => props.altText || props.partnerLogo?.partnerName);

const finalTitle = computed((): string | undefined => props.altText || props.partnerLogo?.partnerName);

const finalHasValidLogo = computed((): boolean =>
  props.hasValidLogo !== undefined ? props.hasValidLogo : !!props.partnerLogo?.partnerLogo?.asset?._ref,
);
</script>

<template>
  <img
    v-if="finalHasValidLogo && finalImage"
    :src="finalImage"
    :alt="finalAltText"
    :title="finalTitle"
    loading="lazy"
    class="c-partner-picture"
  >
</template>

<style scoped>
.c-partner-picture {
  object-fit: contain;
  width: 128px;
  height: 72px;
}
</style>
